import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useStyles} from "./styles/StatisticsStyle";
import {
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Card,
    CardContent,
    FormControl,
    Select,
    MenuItem,
} from "@material-ui/core";
import PropTypes from 'prop-types';
import {inject, observer} from "mobx-react";
import {injectIntl} from "react-intl";
import {ReactComponent as DownloadIcon} from '../../../common/images/DownloadIcon.svg'
import {ReactComponent as AlignLeftIcon} from '../../../common/images/AlignLeftIcon.svg';
import * as XLSX from "xlsx-js-style";
import {DATE_UTIL} from "../../../common/util/date/date.util";

function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <div>{children}</div>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

const logPrefix = "[StatisticsDetail]"

function StatisticsDetail(props) {
    const classes = useStyles();
    const {groupId} = useParams();
    const {authStore, localeStore, statisticsStore} = props;
    const [downloading, setDownloading] = useState(false);
    const [recalculating, setRecalculating] = useState(false);
    const {intl} = props;

    useEffect(() => {
        const fetchData = async () => {
            statisticsStore.setGroupId(groupId);
            await statisticsStore.fetchAvailableMonths(statisticsStore.groupId);
            // 초기 진입은 month 미지정 조회를 사용해 최신 유효 통계 월 폴백을 유지한다.
            await statisticsStore.getUserStudyStatistics(statisticsStore.groupId);
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    const selectedMonthValue = statisticsStore.selectedYear && statisticsStore.selectedMonth
        ? `${statisticsStore.selectedYear}-${statisticsStore.selectedMonth.toString().padStart(2, '0')}`
        : '';
    const studentCountSummary = `${statisticsStore.studiedStudentCount} / ${statisticsStore.totalStudentCount}`;

    const handleMonthChange = async (event) => {
        const value = event.target.value;
        if (!value) {
            return;
        }
        const [year, month] = value.split('-').map((v) => parseInt(v, 10));
        console.log(logPrefix, 'User selected month', value, 'parsed', year, month);
        statisticsStore.setSelectedMonth(year, month);
        await statisticsStore.getUserStudyStatistics(statisticsStore.groupId, year, month);
        console.log(logPrefix, 'After fetch selected', statisticsStore.selectedYear, statisticsStore.selectedMonth);
    };

    const handleRecalculate = () => {
        setRecalculating(true);
        statisticsStore.recalculateUserStudyStatistics(
            statisticsStore.groupId,
            statisticsStore.selectedYear,
            statisticsStore.selectedMonth,
            () => setRecalculating(false)
        );
    };

    const downloadCustomizedExcel = (callback) => {
        console.log(logPrefix, "Download Excel begins");
        try {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const yearSuffix = currentYear.toString().slice(-2);
            const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
            const day = currentDate.getDate().toString().padStart(2, '0');
            const selectedMonthLabel = selectedMonthValue || `${currentYear}-${month}`;

            const workbook = XLSX.utils.book_new();
            const sheetName = `${authStore.loginUser.id}`;
            const startingCell = 'A10';
            const mergeCells = [
                {s: {r: 5, c: 1}, e: {r: 5, c: 2}},
                {s: {r: 6, c: 1}, e: {r: 6, c: 2}},
                {s: {r: 7, c: 1}, e: {r: 7, c: 2}},
                {s: {r: 5, c: 4}, e: {r: 5, c: 5}},
                {s: {r: 6, c: 4}, e: {r: 6, c: 5}},
                {s: {r: 7, c: 4}, e: {r: 7, c: 5}},
            ];

            const filteredData = statisticsStore.userStatistics.map((st) => ({
                userId: st.userId,
                userName: st.userName,
                attendanceRate: st.attendanceRate,
                averagePronunciationScore: st.averagePronunciationScore,
                averageQuizScore: st.averageQuizScore,
            }));


            const worksheet = XLSX.utils.sheet_add_json(XLSX.utils.aoa_to_sheet([[]]), filteredData, {origin: startingCell});
            worksheet['!merges'] = mergeCells;

            if (localeStore.locale === 'en') {
                worksheet['A2'] = {v: 'group name'};
                worksheet['A3'] = {v: 'month'};
                worksheet['A6'] = {v: 'Number of Student'};
                worksheet['A7'] = {v: 'Membership Period'};
                worksheet['D6'] = {v: 'Completion Rate(avg)'};
                worksheet['D7'] = {v: 'Pronunciation Score(avg)'};
                worksheet['D8'] = {v: 'Weekly Quiz(avg)'};
            } else {
                worksheet['A2'] = {v: '그룹 이름'};
                worksheet['A3'] = {v: '월'};
                worksheet['A6'] = {v: '학습자 수'};
                worksheet['A7'] = {v: '멤버십 기간'};
                worksheet['D6'] = {v: '이수율(평균)'};
                worksheet['D7'] = {v: '발음 점수(평균)'};
                worksheet['D8'] = {v: 'Weekly Quiz 점수(평균)'};
            }
            worksheet['B2'] = {v: `${statisticsStore.groupName}`};
            worksheet['B3'] = {v: `${selectedMonthLabel}`};
            worksheet['B6'] = {v: `${studentCountSummary}`};
            worksheet['B7'] = {v: `${statisticsStore.expiredDatetime || '-'}`};
            worksheet['E6'] = {v: `${statisticsStore.avgCompletionRate}%`};
            worksheet['E7'] = {v: `${statisticsStore.avgPronunciationScore}`};
            worksheet['E8'] = {v: `${statisticsStore.avgQuizScore}`};
            worksheet['A10'] = {v: `${intl.formatMessage({id: "email"})}`};
            worksheet['B10'] = {v: `${intl.formatMessage({id: "name"})}`};
            worksheet['C10'] = {v: `${intl.formatMessage({id: "completion_rate"})}`};
            worksheet['D10'] = {v: `${intl.formatMessage({id: "pronunciation_score"})}`};
            worksheet['E10'] = {v: "Weekly Quiz"};

            const columnRange = {min: 1, max: 6};
            const columnWidth = 20;
            worksheet['!cols'] = [];

            for (let i = columnRange.min; i <= columnRange.max; i++) {
                worksheet['!cols'].push({
                    wch: columnWidth
                });
            }

            const boldCellStyle = {
                font: {bold: true},
            };

            ['A2', 'A3', 'B2', 'B3', 'D6'].forEach(cell => {
                worksheet[cell].s = boldCellStyle;
            });

            const headerCellStyle = {
                font: {bold: true},
                fill: {fgColor: {rgb: "87CEEB"}},
                alignment: {horizontal: "center"}
            };

            ['A6', 'A7', 'D6', 'D7', 'D8', 'A10', 'B10', 'C10', 'D10', 'E10'].forEach(cell => {
                worksheet[cell].s = headerCellStyle;
            });

            const centerAlignStyle = {
                alignment: {horizontal: "center"}
            };

            for (let cell in worksheet) {
                if (cell.startsWith('!')) continue;
                if (worksheet[cell].v !== undefined) {
                    worksheet[cell].s = {...worksheet[cell].s, ...centerAlignStyle};
                }
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            const fileMonth = selectedMonthValue ? selectedMonthValue.replace('-', '') : `${yearSuffix}${month}`;
            XLSX.writeFile(workbook, `${authStore.loginUser.id}_${fileMonth}_${day}_${localeStore.locale}.xlsx`);
            console.log(logPrefix, "Download Excel completed");

        } catch (e) {
            console.log(e);
        } finally {
            callback && callback();
        }
    }

    return (
        <Box className={classes.root}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems="center">
                <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>{statisticsStore.groupName}</Typography>
                <Box style={{display: 'flex', alignItems: 'center'}}>
                    <FormControl variant="outlined" className={classes.formControl} style={{marginRight: 8}}>
                        <Select
                            value={selectedMonthValue}
                            onChange={handleMonthChange}
                            displayEmpty
                        >
                            {statisticsStore.availableMonths.map((monthItem) => {
                                const value = `${monthItem.year}-${monthItem.month.toString().padStart(2, '0')}`;
                                return (
                                    <MenuItem key={value} value={value}>
                                        {value}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Button className={classes.buttonStyle}
                            style={{marginRight: 8}}
                            disabled={recalculating || !statisticsStore.selectedYear || !statisticsStore.selectedMonth}
                            onClick={handleRecalculate}
                            disableRipple
                    >
                        {recalculating ? (
                            <CircularProgress size={20}/>
                        ) : (
                            <Typography>{intl.formatMessage({id: "statistics_refresh"})}</Typography>
                        )}
                    </Button>
                    <Button className={classes.buttonStyle}
                            disabled={statisticsStore.userStatistics.length === 0 || downloading}
                            onClick={() => {
                                setDownloading(true);
                                downloadCustomizedExcel(() => setDownloading(false));
                            }}
                            disableRipple
                    >
                        {
                            downloading ?
                                <CircularProgress size={20}/> :
                                <>
                                    <DownloadIcon/>
                                    <Typography>{intl.formatMessage({id: "excelDownload"})}</Typography>
                                </>
                        }
                    </Button>
                </Box>
            </Box>
            <Box>
                <Typography style={{
                    fontWeight: 'bold',
                    fontSize: '1.5rem'
                }}>{intl.formatMessage({id: "enrollment_status"})}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" style={{margin: '20px 0'}}>
                <Box style={{width: '18%'}}>
                    <Card style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '130px'
                    }}>
                        <CardContent>
                            <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                {intl.formatMessage({id: "number_of_student"})}
                            </Typography>
                            <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '2rem'}}>
                                {studentCountSummary}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box style={{width: '18%'}}>
                    <Card style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '130px'
                    }}>
                        <CardContent>
                            <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                {intl.formatMessage({id: "membership_period"})}
                            </Typography>
                            {statisticsStore.expiredDatetime === null ? (
                                <Box>
                                    <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '1rem'}}>
                                        {"-"}
                                    </Typography>
                                    <Typography style={{color: '#FF0000', fontWeight: 'bold', fontSize: '1rem'}}>
                                        ({intl.formatMessage({id: "no_expiration_date"})})
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '1rem'}}>
                                        {DATE_UTIL.getTimeZoneDate(statisticsStore.expiredDatetime, DATE_UTIL.FORMAT.DOT_DATE_FORMAT)}
                                    </Typography>
                                    {statisticsStore.remainingDate > 0 ? (
                                        <Typography style={{color: '#FF0000', fontWeight: 'bold', fontSize: '1rem'}}>
                                            ({intl.formatMessage({id: "expiration"})} D-{statisticsStore.remainingDate})
                                        </Typography>
                                    ) : statisticsStore.remainingDate === 0 ? (
                                        <Typography style={{color: '#FF0000', fontWeight: 'bold', fontSize: '1rem'}}>
                                            ({intl.formatMessage({id: "expiration_date"})})
                                        </Typography>
                                    ) : (
                                        <Typography style={{color: '#FF0000', fontWeight: 'bold', fontSize: '1rem'}}>
                                            ({intl.formatMessage({id: "expired"})})
                                        </Typography>
                                    )}
                                </Box>

                            )}
                        </CardContent>
                    </Card>
                </Box>
                <Box style={{width: '18%'}}>
                    <Card style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '130px'
                    }}>
                        <CardContent>
                            <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                {intl.formatMessage({id: "completion_rate"})}({intl.formatMessage({id: "avg"})})
                            </Typography>
                            <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '2rem'}}>
                                {statisticsStore.avgCompletionRate}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box style={{width: '18%'}}>
                    <Card style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '130px'
                    }}>
                        <CardContent>
                            <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                {intl.formatMessage({id: "pronunciation_score"})}({intl.formatMessage({id: "avg"})})
                            </Typography>
                            <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '2rem'}}>
                                {statisticsStore.avgPronunciationScore}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box style={{width: '18%'}}>
                    <Card style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        height: '130px'
                    }}>
                        <CardContent>
                            <Typography style={{fontWeight: 'bold', fontSize: '1rem'}}>
                                Weekly Quiz({intl.formatMessage({id: "avg"})})
                            </Typography>
                            <Typography style={{color: '#1664F5', fontWeight: 'bold', fontSize: '2rem'}}>
                                {statisticsStore.avgQuizScore}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
            <Box>
                <TableContainer className={classes.tableBox}>
                    <Table className={classes.table} aria-label="simple table">
                        <TableHead className={classes.tableHeadStyle}>
                            <TableRow>
                                <TableCell
                                    align='left'
                                    style={{minWidth: 55}}
                                >
                                    <Box className={classes.sortButton} style={{cursor: 'default'}}>
                                        <AlignLeftIcon/><Typography>{intl.formatMessage({id: "id"})}({intl.formatMessage({id: "email"})})</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell
                                    align='left'
                                    style={{minWidth: 55}}
                                >
                                    <Box className={classes.sortButton} style={{cursor: 'default'}}>
                                        <AlignLeftIcon/><Typography>{intl.formatMessage({id: "name"})}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="left" style={{minWidth: 55}}>
                                    <Box className={classes.sortButton} style={{cursor: 'default'}}>
                                        <AlignLeftIcon/>
                                        <Typography>{intl.formatMessage({id: "completion_rate"})}</Typography>
                                    </Box>
                                </TableCell>
                                {/*<TableCell align="left" style={{minWidth: 55}}>*/}
                                {/*    <Box className={classes.sortButton} style={{cursor: 'default'}}>*/}
                                {/*        <AlignLeftIcon/>*/}
                                {/*        <Typography>{intl.formatMessage({id: "grade"})}</Typography>*/}
                                {/*    </Box>*/}
                                {/*</TableCell>*/}
                                <TableCell align="left" style={{minWidth: 55}}>
                                    <Box className={classes.sortButton} style={{cursor: 'default'}}>
                                        <AlignLeftIcon/>
                                        <Typography>{intl.formatMessage({id: "pronunciation_score"})}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="left" style={{minWidth: 55}}>
                                    <Box className={classes.sortButton} style={{cursor: 'default'}}>
                                        <AlignLeftIcon/><Typography>Final Test</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className={classes.tableBodyStyle}>
                            {statisticsStore.userStatistics.map((student, index) => (
                                <TableRow key={index}>
                                    <TableCell component="th" scope="row"
                                               align='left'>{student.userId}</TableCell>
                                    <TableCell align="left">{student.userName}</TableCell>
                                    <TableCell align="left">{student.attendanceRate} %</TableCell>
                                    {/*<TableCell align="left">{student.grade}</TableCell>*/}
                                    <TableCell align="left">{student.averagePronunciationScore}</TableCell>
                                    <TableCell align="left">{student.averageQuizScore}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    )
        ;
}

export default inject('authStore', 'localeStore', 'statisticsStore')(injectIntl(observer(StatisticsDetail)));
